import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, type ReactNode } from 'react'

import { onUnauthorized } from '@/api/client'
import { Spinner } from '@/components/ui/spinner'

import { authStatusKey, fetchAuthStatus } from './api'
import { LoginScreen } from './LoginScreen'

/**
 * Renders the application only for an authenticated owner; otherwise the login
 * screen. Kept above the router outlet so no route has to think about auth, and
 * so a session that expires mid-session drops straight back to the login screen
 * rather than showering the user with error toasts.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const { data, isPending, isError } = useQuery({
    queryKey: authStatusKey,
    queryFn: fetchAuthStatus,
    staleTime: 0,
    retry: false,
  })

  useEffect(
    () =>
      onUnauthorized(() => {
        queryClient.setQueryData(authStatusKey, (previous: unknown) => {
          const known = previous as { enrollment_open?: boolean } | undefined
          return { authenticated: false, enrollment_open: known?.enrollment_open ?? false }
        })
        void queryClient.invalidateQueries({ queryKey: authStatusKey })
      }),
    [queryClient],
  )

  if (isPending) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spinner label="Checking your session" />
      </div>
    )
  }

  // If the status probe itself fails the backend is unreachable; showing the
  // login screen is the honest fallback, since nothing else can be trusted.
  if (isError || !data?.authenticated) {
    return <LoginScreen enrollmentOpen={data?.enrollment_open ?? false} />
  }

  return <>{children}</>
}
